import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserManagementTable = ({ users, currentUserId, onRoleChange, onStatusToggle, onConfirmUser, onRevokeConfirmation, loading }) => {
  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-error/10 text-error border-error/20',
      scout: 'bg-accent/10 text-accent border-accent/20',
      club: 'bg-warning/10 text-warning border-warning/20',
      player: 'bg-primary/10 text-primary border-primary/20',
      coach: 'bg-secondary/10 text-secondary border-secondary/20',
      keeper: 'bg-success/10 text-success border-success/20'
    };
    return colors?.[role] || 'bg-muted/10 text-muted-foreground border-muted/20';
  };

  const formatRole = (role) => {
    return role?.charAt(0)?.toUpperCase() + role?.slice(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (!users || users?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Users Found</h3>
        <p className="text-muted-foreground">No users match your current filters</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Organization
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Confirmation
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users?.map((user) => (
              <tr key={user?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {user?.avatar_url ? (
                        <img 
                          src={user?.avatar_url} 
                          alt={user?.full_name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-primary font-semibold">
                          {user?.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {user?.full_name}
                        {user?.id === currentUserId && (
                          <span className="ml-2 text-xs text-primary">(You)</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user?.role)}`}>
                    {formatRole(user?.role)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-foreground">
                    {user?.organization || 'N/A'}
                  </p>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      user?.is_active ? 'bg-success' : 'bg-error'
                    }`} />
                    <span className={`text-sm ${
                      user?.is_active ? 'text-success' : 'text-error'
                    }`}>
                      {user?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {['player', 'coach', 'keeper']?.includes(user?.role) ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          user?.is_confirmed ? 'bg-success' : 'bg-warning'
                        }`} />
                        <span className={`text-sm ${
                          user?.is_confirmed ? 'text-success' : 'text-warning'
                        }`}>
                          {user?.is_confirmed ? 'Confirmed' : 'Pending'}
                        </span>
                      </div>
                      {user?.is_confirmed && user?.assigned_profile && (
                        <p className="text-xs text-muted-foreground">
                          Profile: {user?.assigned_profile?.full_name}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">N/A</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-muted-foreground">
                    {formatDate(user?.created_at)}
                  </p>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    {['player', 'coach', 'keeper']?.includes(user?.role) && (
                      user?.is_confirmed ? (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onRevokeConfirmation(user?.id)}
                          disabled={user?.id === currentUserId}
                          iconName="XCircle"
                          iconSize={14}
                        >
                          Revoke
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => onConfirmUser(user)}
                          disabled={user?.id === currentUserId}
                          iconName="CheckCircle"
                          iconSize={14}
                        >
                          Confirm
                        </Button>
                      )
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onRoleChange(user)}
                      disabled={user?.id === currentUserId}
                      iconName="Edit"
                      iconSize={14}
                    >
                      Role
                    </Button>
                    <Button
                      size="sm"
                      variant={user?.is_active ? 'destructive' : 'default'}
                      onClick={() => onStatusToggle(user?.id, user?.is_active)}
                      disabled={user?.id === currentUserId}
                      iconName={user?.is_active ? 'XCircle' : 'CheckCircle'}
                      iconSize={14}
                    >
                      {user?.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagementTable;